import { useState } from 'react';
import { useTheme, Button, Modal } from '@crackpoint/shared/components';
import { useVersion } from '@crackpoint/shared/hooks';
import { getBranding, strings } from '@crackpoint/shared/config';

interface MenuScreenProps {
  onStartGame: () => void;
  onShowLeaderboard: () => void;
  onShowSettings: () => void;
  isLoading?: boolean;
  className?: string;
}

export function MenuScreen({
  onStartGame,
  onShowLeaderboard,
  onShowSettings,
  isLoading = false,
}: MenuScreenProps) {
  const { theme } = useTheme();
  const [showAbout, setShowAbout] = useState(false);
  const branding = getBranding();
  const version = useVersion();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: theme.spacing.lg,
        backgroundColor: theme.colours.background.primary,
        gap: theme.spacing.xl,
        fontFamily: "'Courier New', 'Lucida Console', monospace",
      }}
    >
      <div
        style={{
          textAlign: 'center',
          marginBottom: theme.spacing.xl,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 'clamp(2rem, 8vw, 4rem)',
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colours.text.primary,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          {strings.menu.title}
        </h1>
        <p
          style={{
            margin: theme.spacing.md,
            fontSize: theme.typography.fontSize.lg,
            color: theme.colours.text.secondary,
          }}
        >
          {branding.productName}
        </p>
        <p
          style={{
            margin: 0,
            fontSize: theme.typography.fontSize.sm,
            color: theme.colours.text.tertiary,
            fontStyle: 'italic',
          }}
        >
          {branding.tagline}
        </p>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.md,
          width: '100%',
          maxWidth: '320px',
        }}
      >
        <Button
          size="lg"
          fullWidth
          onClick={onStartGame}
          disabled={isLoading}
          loading={isLoading}
          style={{
            fontSize: theme.typography.fontSize.xl,
            padding: `${theme.spacing.lg} ${theme.spacing.xl}`,
          }}
        >
          {strings.menu.startButton}
        </Button>

        <Button
          variant="secondary"
          size="lg"
          fullWidth
          onClick={onShowLeaderboard}
        >
          {strings.menu.leaderboardButton}
        </Button>

        <Button
          variant="secondary"
          size="lg"
          fullWidth
          onClick={onShowSettings}
        >
          {strings.menu.settingsButton}
        </Button>

        <Button
          variant="ghost"
          size="lg"
          fullWidth
          onClick={() => setShowAbout(true)}
        >
          {strings.menu.aboutButton}
        </Button>
      </div>

      <Modal
        isOpen={showAbout}
        onClose={() => setShowAbout(false)}
        title={strings.about.title(branding.productFullName)}
        footer={
          <Button variant="primary" onClick={() => setShowAbout(false)}>
            {strings.menu.closeButton}
          </Button>
        }
      >
        <div style={{ color: theme.colours.text.secondary }}>
          <p style={{ marginBottom: theme.spacing.md }}>
            {strings.about.description}
          </p>
          <p style={{ marginBottom: theme.spacing.md }}>
            <strong>{strings.about.howToPlayTitle}</strong>
          </p>
          <ul
            style={{
              paddingLeft: theme.spacing.lg,
              marginBottom: theme.spacing.md,
            }}
          >
            <li>{strings.about.howToPlay.interceptSignals}</li>
            <li>{strings.about.howToPlay.submitCodes}</li>
            <li>{strings.about.howToPlay.clearRooms}</li>
            <li>{strings.about.howToPlay.rarerSignals}</li>
            <li>{strings.about.howToPlay.watchLives}</li>
          </ul>
          <p style={{ marginBottom: 0, fontSize: theme.typography.fontSize.sm }}>
            {strings.about.versionLabel(branding.productName, version || '...')}
          </p>
        </div>
      </Modal>
    </div>
  );
}
